/**
 * Contact Us page - Green/black theme, PublicPageLayout.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n, useToast } from '../providers';
import { PublicPageLayout } from '../components/public';
import { submitContact } from '../services/contact';

const ContactUs = () => {
  const navigate = useNavigate();
  const { language } = useI18n();
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setSubmitMessage('');
    try {
      setIsSubmitting(true);
      await submitContact(formData);
      const successMsg =
        language === 'fr'
          ? 'Votre message a été envoyé avec succès! Nous vous répondrons dans les 24 heures.'
          : "Your message has been sent successfully! We'll respond within 24 hours.";
      setSubmitStatus('success');
      setSubmitMessage(successMsg);
      showSuccess(successMsg);
      setFormData({ name: '', email: '', subject: '', category: 'general', message: '' });
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        (language === 'fr' ? "Échec de l'envoi du message" : 'Failed to send message');
      setSubmitStatus('error');
      setSubmitMessage(errMsg);
      showError(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCategories = [
    { value: 'general', label: language === 'fr' ? 'Général' : 'General' },
    { value: 'technical', label: language === 'fr' ? 'Support Technique' : 'Technical Support' },
    { value: 'business', label: language === 'fr' ? 'Affaires' : 'Business' },
    { value: 'press', label: language === 'fr' ? 'Presse' : 'Press' },
    { value: 'partnership', label: language === 'fr' ? 'Partenariat' : 'Partnership' },
    { value: 'other', label: language === 'fr' ? 'Autre' : 'Other' },
  ];

  const emailSubject = language === 'fr' ? 'Contact WekaTrust - Site web' : 'WekaTrust Contact - Website';
  const contactMethods = [
    { icon: '📧', title: language === 'fr' ? 'Email' : 'Email', value: 'support@wekatrust.com', description: language === 'fr' ? 'Réponse sous 24h' : 'Response within 24h', link: `mailto:support@wekatrust.com?subject=${encodeURIComponent(emailSubject)}` },
    { icon: '📱', title: language === 'fr' ? 'WhatsApp' : 'WhatsApp', value: '+237 655 536 083', description: language === 'fr' ? 'Support en direct' : 'Live support', link: 'https://wa.me/237655536083', external: true },
    { icon: '🌐', title: language === 'fr' ? 'Site Web' : 'Website', value: 'www.wekatrust.com', description: language === 'fr' ? 'Informations générales' : 'General information', link: null },
  ];

  return (
    <PublicPageLayout>
      <button type="button" onClick={() => navigate('/')} className="public-page-back">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        {language === 'fr' ? "Retour à l'accueil" : 'Back to Home'}
      </button>

      <div className="public-page-hero">
        <h1 className="public-page-title">{language === 'fr' ? 'Contactez-Nous' : 'Contact Us'}</h1>
        <p className="public-page-sub">
          {language === 'fr' ? "Nous sommes là pour vous aider" : "We're here to help you"}
        </p>
      </div>

      <div className="public-page-section">
        <div className="public-page-grid" style={{ marginBottom: '2rem' }}>
          {contactMethods.map((method, i) => {
            const Wrapper = method.link ? 'a' : 'div';
            const wrapperProps = method.link
              ? {
                  href: method.link,
                  ...(method.external && { target: '_blank', rel: 'noopener noreferrer' }),
                  className: 'public-page-contact-card public-page-contact-card-link',
                }
              : { className: 'public-page-contact-card' };
            return (
              <Wrapper key={i} {...wrapperProps}>
                <div className="public-page-contact-icon">{method.icon}</div>
                <h3>{method.title}</h3>
                <p style={{ fontWeight: 600, color: 'var(--pub-text)', marginBottom: '0.25rem' }}>{method.value}</p>
                <p>{method.description}</p>
              </Wrapper>
            );
          })}
        </div>
      </div>

      <div className="public-page-section">
        <div className="public-page-card" style={{ padding: '1.75rem' }}>
          <h2 className="public-page-section-title" style={{ marginBottom: '1.25rem' }}>
            {language === 'fr' ? 'Envoyez-nous un Message' : 'Send Us a Message'}
          </h2>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="public-page-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <div>
                <label>{language === 'fr' ? 'Nom Complet' : 'Full Name'} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                  required
                />
              </div>
              <div>
                <label>{language === 'fr' ? 'Email' : 'Email'} *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={language === 'fr' ? 'votre@email.com' : 'your@email.com'}
                  required
                />
              </div>
            </div>
            <div>
              <label>{language === 'fr' ? 'Catégorie' : 'Category'} *</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                required
              >
                {contactCategories.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label>{language === 'fr' ? 'Sujet' : 'Subject'} *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder={language === 'fr' ? 'Résumé de votre demande' : 'Summary of your inquiry'}
                required
              />
            </div>
            <div>
              <label>{language === 'fr' ? 'Message' : 'Message'} *</label>
              <textarea
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder={language === 'fr' ? 'Décrivez votre demande en détail...' : 'Describe your inquiry in detail...'}
                rows={6}
                required
              />
            </div>
            <button type="submit" className="public-page-cta" disabled={isSubmitting} style={{ alignSelf: 'flex-start' }}>
              {isSubmitting
                ? (language === 'fr' ? 'Envoi en cours...' : 'Sending...')
                : (language === 'fr' ? 'Envoyer le Message' : 'Send Message')}
            </button>
            {submitStatus === 'success' && (
              <p role="alert" className="public-page-success-msg" style={{ marginTop: '1rem', color: 'var(--pub-green)', fontWeight: 600 }}>
                {submitMessage}
              </p>
            )}
            {submitStatus === 'error' && (
              <p role="alert" className="public-page-error-msg" style={{ marginTop: '1rem', color: '#ef4444', fontWeight: 600 }}>
                {submitMessage}
              </p>
            )}
          </form>
        </div>
      </div>

      <div className="public-page-card" style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        <div className="public-page-contact-icon" style={{ flexShrink: 0 }}>⏱️</div>
        <div>
          <h3 className="public-page-card-title">{language === 'fr' ? 'Temps de Réponse' : 'Response Time'}</h3>
          <p className="public-page-card-desc" style={{ margin: 0 }}>
            {language === 'fr'
              ? "Nous nous efforçons de répondre à toutes les demandes dans les 24 heures. Pour les demandes urgentes, utilisez WhatsApp pour un support en direct."
              : 'We strive to respond to all inquiries within 24 hours. For urgent requests, use WhatsApp for live support.'}
          </p>
        </div>
      </div>
    </PublicPageLayout>
  );
};

export default ContactUs;
